/**
 * Presentational guarantor-status rows — shared by the individual header
 * popover (HeaderScorePanel) and the combined GuarantorStatusDialog. Binds the
 * pure `ApplicantGuarantors` / `GuarantorEntry` DTO (guarantor-requests.ts);
 * zero data logic here. "Accepted" reflects the INVITE; once accepted, the row
 * also surfaces the guarantor's own screening score / progress.
 *
 * TT-42 (decision D3): rows whose invite is still live (`invitation_sent`)
 * grow a "Cancel invite" affordance when the caller passes `cancelInvite`
 * (gated upstream by `can.cancelInvitation` + decision-open). The button owns
 * its fetcher — the one mutation this component performs (house rule: dialogs/
 * buttons own the fetcher for their own mutation only).
 */
import { CircleAlert, CircleCheck, Loader2, UserCheck, X } from 'lucide-react'
import { useState } from 'react'
import { useFetcher } from 'react-router'
import type {
	ApplicantGuarantors,
	GuarantorEntry,
} from '#app/utils/application-detail-v2/guarantor-requests'
import type { DetailActionResult } from '#app/utils/application-detail-v2/types'
import { cn } from '#app/utils/misc'
import { ActionResultMarker, CancelInviteConfirm } from './dialogs/dialog-bits'

/** Passed down from surfaces that may cancel a live invite. Absent = no
 * affordance (read-only surfaces, decided applications, missing permission). */
export interface CancelInviteContext {
	applicationId: string
}

// Keyed by the landlord-decision-centric `GuarantorDisplayStatus`
// (guarantor-requests.ts), NOT the raw invite status.
const STATUS_PILL: Record<
	string,
	{ cls: string; label: string; icon: React.ReactNode }
> = {
	invitation_sent: {
		cls: 'text-[#F59F0A]',
		label: 'Invitation Sent',
		icon: <CircleCheck className="size-4" />,
	},
	pending: {
		cls: 'text-[#F59F0A]',
		label: 'Pending',
		icon: <Loader2 className="size-4" />,
	},
	accepted: {
		cls: 'text-[#16A34A]',
		label: 'Accepted',
		icon: <UserCheck className="size-4" />,
	},
	declined: {
		cls: 'text-[#DC2634]',
		label: 'Declined',
		icon: <CircleAlert className="size-4" />,
	},
	withdrawn: {
		cls: 'text-[#747474]',
		label: 'Withdrawn',
		icon: <CircleAlert className="size-4" />,
	},
	abandoned: {
		cls: 'text-[#747474]',
		label: 'Abandoned',
		icon: <CircleAlert className="size-4" />,
	},
	expired: {
		cls: 'text-[#DC2634]',
		label: 'Expired',
		icon: <CircleAlert className="size-4" />,
	},
	cancelled: {
		cls: 'text-[#DC2634]',
		label: 'Cancelled',
		icon: <CircleAlert className="size-4" />,
	},
}

function StatusPill({ status }: { status: string }) {
	const pill = STATUS_PILL[status] ?? STATUS_PILL.invitation_sent
	return (
		<span
			className={cn(
				'inline-flex items-center gap-1 text-xs font-medium',
				pill.cls,
			)}
		>
			{pill.icon}
			{pill.label}
		</span>
	)
}

/** TT-42 cancel affordance — owns its fetcher; posts to the page's action.
 * Exported for reuse on the combined overview's InviteeTiles (2026-07-13) —
 * one cancel control everywhere, never a per-surface fork. Success/error
 * toast via the shared marker ("Invitation cancelled", 2026-07-13 — the
 * row/tile disappearing alone read as nothing happening); the inline error
 * line stays for the popover context. */
export function CancelInviteButton({
	applicationId,
	invitationId,
	variant = 'link',
	invitedLabel = 'The invitee',
}: {
	applicationId: string
	invitationId: string
	/** 'link' — the popover's inline "Cancel invite" text; 'icon' — the
	 * combined overview's top-right ✕ (Figma 2026-09-07), the same control
	 * the member tiles carry. Same confirm, same intent either way. */
	variant?: 'link' | 'icon'
	/** Who the confirm names (defaults to the popover's generic wording). */
	invitedLabel?: string
}) {
	const fetcher = useFetcher<DetailActionResult>()
	const [confirmOpen, setConfirmOpen] = useState(false)
	const busy = fetcher.state !== 'idle'
	// Discriminated union: only the ok:false arm carries `error`.
	const failure = fetcher.data && !fetcher.data.ok ? fetcher.data : null
	return (
		<>
			<ActionResultMarker
				fetcher={fetcher}
				fallback="Invitation cancelled"
				onSuccess={() => setConfirmOpen(false)}
			/>
			{/* Irreversible + un-resendable (D3) — never on a single click
			    (QA 2026-07-18; matches the All Applicants side panel). */}
			<CancelInviteConfirm
				open={confirmOpen}
				onOpenChange={setConfirmOpen}
				invitedLabel={invitedLabel}
				busy={busy}
				onConfirm={() =>
					void fetcher.submit(
						{ intent: 'cancelInvitation', applicationId, invitationId },
						{ method: 'POST' },
					)
				}
			/>
			{variant === 'icon' ? (
				<button
					type="button"
					disabled={busy}
					onClick={() => setConfirmOpen(true)}
					aria-label={`Cancel invitation for ${invitedLabel}`}
					className="absolute top-3 right-3 flex size-6 cursor-pointer items-center justify-center text-[#32302F] transition-opacity hover:opacity-60 disabled:cursor-not-allowed disabled:opacity-50"
				>
					<X className="size-4.5" strokeWidth={2} />
				</button>
			) : (
				<button
					type="button"
					disabled={busy}
					onClick={() => setConfirmOpen(true)}
					className="cursor-pointer text-xs font-medium text-[#DC2634] underline-offset-2 hover:underline disabled:cursor-not-allowed disabled:opacity-50"
				>
					{busy ? 'Cancelling…' : 'Cancel invite'}
				</button>
			)}
			{failure ? (
				<p className="text-xs text-[#DC2634]">{failure.error}</p>
			) : null}
		</>
	)
}

function GuarantorEntryRow({
	entry,
	cancelInvite,
}: {
	entry: GuarantorEntry
	cancelInvite?: CancelInviteContext
}) {
	const canCancel = cancelInvite && entry.displayStatus === 'invitation_sent'
	return (
		<div className="flex items-center justify-between gap-3 rounded-xl border border-[#F0F0F0] bg-[#FBFDFD] p-3">
			<div className="flex min-w-0 items-center gap-3">
				<div className="min-w-0">
					<p className="font-regular truncate text-sm text-[#32302F]">
						{entry.name}
					</p>
				</div>
			</div>
			<div className="flex shrink-0 flex-col items-end gap-1 text-right">
				<StatusPill status={entry.displayStatus} />
				{canCancel ? (
					<CancelInviteButton
						applicationId={cancelInvite.applicationId}
						invitationId={entry.id}
					/>
				) : null}
			</div>
		</div>
	)
}

/** One requesting applicant's guarantor(s). `showApplicant` adds the applicant
 * heading + per-applicant "N of M accepted" (combined modal); the individual
 * popover omits it (single applicant, count shown in the chip). */
export function GuarantorApplicantSection({
	applicant,
	showApplicant = false,
	cancelInvite,
}: {
	applicant: ApplicantGuarantors
	showApplicant?: boolean
	/** TT-42: enables "Cancel invite" on live-invite rows. */
	cancelInvite?: CancelInviteContext
}) {
	return (
		<div className="space-y-2">
			{showApplicant ? (
				<p className="flex items-baseline justify-between text-sm font-semibold text-[#32302F]">
					{applicant.applicantName}
					<span className="text-xs font-normal text-[#747474]">
						{applicant.accepted} of {applicant.total} accepted
					</span>
				</p>
			) : null}
			{applicant.guarantors.map((entry) => (
				<GuarantorEntryRow
					key={entry.id}
					entry={entry}
					cancelInvite={cancelInvite}
				/>
			))}
		</div>
	)
}
